import { useNavigation } from "@react-navigation/native";
import { Formik } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { ONBOARDING_NAVIGATION_ERROR } from "../../constants/onboardingMessages";
import ScreenNames from "../../constants/routeNames";
import { getNextScreen } from "../../navigation/simpleOnboardingFlow";
import type { OnboardingMotivationEnum } from "../../services/backendTypes";
import { selectMotivation, setMotivation } from "../../slices/simpleOnboardingSlice";
import SelectionScreen, { type Option } from "../New_DIY_form/SelectionScreen";

const validationSchema = Yup.object().shape({
  motivation: Yup.string()
    .oneOf(["EAT_HEALTHIER", "RECIPE_INSPIRATION", "SAVE_TIME", "MAKE_EATING_FUN", "BUDGET_CONSCIOUS"])
    .required("Please select your motivation"),
});

const SimpleOnboardingMotivationScreen = (): JSX.Element => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const dispatch = useDispatch();

  const selectedMotivation = useSelector(selectMotivation);

  const options: Option[] = [
    {
      value: "EAT_HEALTHIER" as OnboardingMotivationEnum,
      label: t("simple_onboarding.motivation.EAT_HEALTHIER"),
      description: "",
      icon: "HeartPulse",
      iconType: "lucide",
    },
    {
      value: "RECIPE_INSPIRATION" as OnboardingMotivationEnum,
      label: t("simple_onboarding.motivation.RECIPE_INSPIRATION"),
      description: "",
      icon: "Lightbulb",
      iconType: "lucide",
    },
    {
      value: "SAVE_TIME" as OnboardingMotivationEnum,
      label: t("simple_onboarding.motivation.SAVE_TIME"),
      description: "",
      icon: "Timer",
      iconType: "lucide",
    },
    {
      value: "MAKE_EATING_FUN" as OnboardingMotivationEnum,
      label: t("simple_onboarding.motivation.MAKE_EATING_FUN"),
      description: "",
      icon: "Sparkles",
      iconType: "lucide",
    },
    {
      value: "BUDGET_CONSCIOUS" as OnboardingMotivationEnum,
      label: t("simple_onboarding.motivation.BUDGET_CONSCIOUS"),
      description: "",
      icon: "DollarSign",
      iconType: "lucide",
    },
  ];

  return (
    <Formik
      initialValues={{ motivation: selectedMotivation || "" }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        dispatch(setMotivation(values.motivation as OnboardingMotivationEnum));
        const nextScreen = getNextScreen(ScreenNames.SimpleOnboarding_1_Motivation);
        if (nextScreen) {
          navigation.navigate(nextScreen as never);
        } else {
          alert(ONBOARDING_NAVIGATION_ERROR);
        }
      }}
    >
      {({ values, setFieldValue, errors, handleSubmit }) => (
        <SelectionScreen
          title={t("simple_onboarding.motivation.title")}
          subtitle={t("simple_onboarding.motivation.subtitle")}
          options={options}
          multipleSelection={false}
          selectedOptions={values.motivation ? [values.motivation] : []}
          setSelectedOptions={(motivationOptions) => setFieldValue("motivation", motivationOptions[0])}
          onPressNext={handleSubmit}
          onPressBack={() => navigation.goBack()}
          nextButtonDisabled={!values.motivation}
          errorMessage={errors.motivation}
          nextButtonTestIdPostfix="motivation"
        />
      )}
    </Formik>
  );
};

export default SimpleOnboardingMotivationScreen;
